import {
  Box,
  Button,
  Typography,
  Grid2 as Grid,
  Switch,
  Paper,
  Alert,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useGetPartnerDropdownsQuery } from "src/shared/api/dropdowns/partner";
import { Loader, SelectField } from "src/shared/components";
import { IconComponent } from "src/shared/components/IconComponent";
import { useConfirmModal } from "src/shared/hooks";
import { useAuth } from "src/shared/lib/rback/AuthContext";
import { EAgencies } from "src/shared/types/common.types";
import {
  useGetServiceToolsQuery,
  useUpdateServiceToolsMutation,
  useGetLastUpdateQuery,
  useCheckLaunchedProcessesQuery,
  useLazyCheckLaunchedProcessesQuery,
} from "../model/api/api";
import {
  COLUMNS,
  partnershipTypeOptions,
  SERVICE_ROWS,
  TOOL_TO_COLUMN,
} from "./constants";
import {
  ColumnKey,
  CooperationValue,
  FormValues,
  ServiceToolDto,
  ServiceToolsResponse,
  SwitchKey,
  ToolsByRow,
} from "./types";

export const ServiceTools = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("system:servicetools:edit");
  const readOnly = !canEdit;

  const [agencyFilter, setAgencyFilter] = useState<EAgencies>(
    EAgencies.Webcom_BY,
  );
  const [ptFilter, setPtFilter] = useState<CooperationValue>("our_management");
  const [isCancelling, setIsCancelling] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const pendingActionRef = useRef<null | (() => void | Promise<void>)>(null);
  const initialIdMapRef = useRef<
    Record<string, { id: number; is_enabled: boolean }>
  >({});

  const { data: dropdowns, isLoading: isLoadingDropdowns } =
    useGetPartnerDropdownsQuery();
  const agencyOptions = dropdowns?.agency ?? [];

  const { data, isFetching, isLoading, refetch } = useGetServiceToolsQuery(
    { agency: agencyFilter, cooperation_type: ptFilter },
    { refetchOnMountOrArgChange: true },
  );

  const [updateServiceTools, { isLoading: isUpdating }] =
    useUpdateServiceToolsMutation();
  const { data: lastUpdateData, isLoading: isLastUpdateLoading } =
    useGetLastUpdateQuery(null);

  const {
    data: checkLaunchedProcessesData,
    isLoading: checkLaunchedProcessesLoading,
  } = useCheckLaunchedProcessesQuery(undefined);
  const [checkLauncedProcesses] = useLazyCheckLaunchedProcessesQuery();

  const isLaunched = !!checkLaunchedProcessesData?.data?.is_launched;

  const formatRuDate = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";

    return new Intl.DateTimeFormat("ru-RU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  };

  const rowsMeta = useMemo(() => Object.values(SERVICE_ROWS), []);

  const methods = useForm<FormValues>({ defaultValues: { tools: {} } });
  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const buildToolsFromApi = useCallback(
    (api: ServiceToolDto[]) => {
      const next: ToolsByRow = {};
      for (const r of rowsMeta) {
        next[r.id] = COLUMNS.reduce(
          (acc, c) => {
            acc[c.key] = false;

            return acc;
          },
          {} as Record<ColumnKey, boolean>,
        );
      }
      for (const item of api) {
        const rowId = SERVICE_ROWS[item.advertising_service]?.id;
        const colKey = TOOL_TO_COLUMN[item.service_tool.name];
        if (!rowId || !colKey) continue;
        next[rowId][colKey] = !!item.is_enabled;
      }

      return next;
    },
    [rowsMeta],
  );

  useEffect(() => {
    const api = (data as ServiceToolsResponse | undefined)?.data;
    if (!api) return;

    const nextTools: ToolsByRow = {};
    for (const r of rowsMeta) {
      nextTools[r.id] = COLUMNS.reduce(
        (acc, c) => {
          acc[c.key] = false;

          return acc;
        },
        {} as Record<ColumnKey, boolean>,
      );
    }

    const nextIdMap: Record<string, { id: number; is_enabled: boolean }> = {};

    for (const item of api) {
      const rowId = SERVICE_ROWS[item.advertising_service]?.id;
      const colKey = TOOL_TO_COLUMN[item.service_tool.name];
      if (!rowId || !colKey) continue;

      nextTools[rowId][colKey] = !!item.is_enabled;
      const key: SwitchKey = `${rowId}:${colKey}`;
      nextIdMap[key] = { id: item.id, is_enabled: !!item.is_enabled };
    }

    reset({ tools: nextTools });
    initialIdMapRef.current = nextIdMap;
  }, [data, reset, rowsMeta]);

  const { modal: saveChangesModal, showModal: showSaveChangesModal } =
    useConfirmModal({
      resolveOnCancel: true,
      resolveOnClose: true,
      width: 560,
      title: "Вы изменили данные, но ещё не сохранили их",
      children: (
        <Typography>
          При смене фильтра без сохранения внесённые изменения будут потеряны.
        </Typography>
      ),
      confirmBtnProps: {
        children: "Сохранить и продолжить",
        isLoading: isSaving,
      },
      cancelBtnProps: { children: "Продолжить без сохранения" },
      onResolve: async ({ result, closeModal }) => {
        try {
          if (result) {
            setIsSaving(true);
            await handleSubmit(onSubmit)();
          }
          const action = pendingActionRef.current;
          if (action) await action();
        } finally {
          setIsSaving(false);
          pendingActionRef.current = null;
          closeModal();
        }
      },
    });

  const handleCancel = async () => {
    try {
      setIsCancelling(true);
      const res = await refetch();
      const api = (res.data as ServiceToolsResponse | undefined)?.data;
      if (api) reset({ tools: buildToolsFromApi(api) }, { keepDirty: false });
    } finally {
      setIsCancelling(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (readOnly) return;

    const changes: Array<{ id: number; is_enabled: boolean }> = [];
    const changedKeys: Array<SwitchKey> = [];

    for (const [rowId, cols] of Object.entries(values.tools)) {
      (Object.keys(cols) as ColumnKey[]).forEach((colKey) => {
        const key =
          `${rowId as keyof typeof SERVICE_ROWS}:${colKey}` as SwitchKey;
        const initial = initialIdMapRef.current[key];
        if (!initial) return;

        const current = !!cols[colKey];
        if (current !== initial.is_enabled) {
          changes.push({ id: initial.id, is_enabled: current });
          changedKeys.push(key);
        }
      });
    }

    if (changes.length === 0) return;

    const payload = {
      agency: agencyFilter,
      cooperation_type: ptFilter,
      data: changes,
    };

    try {
      await updateServiceTools(payload).unwrap();

      const currentValues = methods.getValues();
      reset(currentValues, { keepDirty: false });

      changedKeys.forEach((key) => {
        const [rowId, colKey] = key.split(":") as [
          keyof typeof SERVICE_ROWS,
          ColumnKey,
        ];
        const current = !!currentValues.tools[rowId][colKey];
        const prev = initialIdMapRef.current[key];
        if (prev)
          initialIdMapRef.current[key] = { ...prev, is_enabled: current };
      });

      enqueueSnackbar({ variant: "success", message: "Изменения сохранены" });
      await checkLauncedProcesses(undefined).unwrap();
    } catch (e) {
      enqueueSnackbar({
        variant: "error",
        message: "Что-то пошло не так. Попробуйте позже",
      });
      console.error(e);
    }
  };

  const runGuarded = (action: () => void | Promise<void>) => {
    if (readOnly || !methods.formState.isDirty) return void action();
    pendingActionRef.current = action;
    showSaveChangesModal();
  };

  const showActions = canEdit && isDirty && !isFetching && !isCancelling;

  if (
    isLoadingDropdowns ||
    isFetching ||
    isUpdating ||
    isCancelling ||
    checkLaunchedProcessesLoading
  ) {
    return <Loader position="absolute" blur size={32} />;
  }

  return (
    <>
      <Box sx={{ display: "flex", gap: 3, mt: 2 }}>
        <Typography variant="h6">Справочник Service Tools</Typography>
        {showActions && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 3,
              ml: "auto",
            }}
          >
            <Button
              onClick={handleCancel}
              variant="outlined"
              disabled={isCancelling || isFetching}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              form="service-tools-form"
              variant="contained"
              disabled={isCancelling || isUpdating}
            >
              Сохранить
            </Button>
          </Box>
        )}
      </Box>

      {isLaunched ? (
        <Box sx={{ mt: 2, zIndex: (t) => t.zIndex.modal + 1 }}>
          <Alert variant="filled" severity="warning">
            Обновляем данные, чтобы всё отображалось корректно. Вернитесь через
            пару минут и перезагрузите страницу.
          </Alert>
        </Box>
      ) : (
        <>
          <Grid
            container
            columns={{ xs: 12 }}
            spacing={2}
            sx={{ alignItems: "center", mt: 10 }}
          >
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "nowrap",
                  overflowX: { xs: "auto", md: "visible" },
                  whiteSpace: "nowrap",
                  "& > *": { flex: "0 0 auto" },
                }}
              >
                <Box sx={{ minWidth: 250 }}>
                  <SelectField<EAgencies>
                    label="Компания"
                    value={agencyFilter}
                    onChange={(e) => {
                      const next = Number(e?.target?.value) as EAgencies;
                      runGuarded(() => setAgencyFilter(next));
                    }}
                    options={agencyOptions}
                  />
                </Box>
                <Box sx={{ minWidth: 240 }}>
                  <SelectField<CooperationValue>
                    label="Тип сотрудничества"
                    value={ptFilter}
                    onChange={(e) => {
                      const next = e?.target?.value as CooperationValue;
                      runGuarded(() => setPtFilter(next));
                    }}
                    options={partnershipTypeOptions}
                  />
                </Box>
              </Box>
            </Grid>

            {/* ✅ Шапка с лейблами колонок */}
            {COLUMNS.map((c) => (
              <Grid
                key={c.key}
                sx={{ flex: 1, display: "flex", justifyContent: "center" }}
              >
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: 14,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.label}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <FormProvider {...methods}>
            <Box
              component="form"
              id="service-tools-form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ position: "relative" }}
            >
              {(isFetching || isCancelling || isLoading) && (
                <Loader position="absolute" blur size={32} />
              )}

              {rowsMeta.map((row) => (
                <Paper
                  key={row.id}
                  variant="outlined"
                  elevation={0}
                  sx={{
                    p: 5,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    mt: 3,
                    opacity: isCancelling ? 0.6 : 1,
                  }}
                >
                  <Grid
                    container
                    columns={{ xs: 12 }}
                    spacing={2}
                    sx={{ alignItems: "center" }}
                  >
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          minWidth: 0,
                        }}
                      >
                        <IconComponent iconname={row.logo} fontSize="20px" />
                        <Typography noWrap title={row.name}>
                          {row.name}
                        </Typography>
                      </Box>
                    </Grid>

                    {COLUMNS.map((c) => (
                      <Grid
                        key={c.key}
                        sx={{
                          flex: 1,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Controller
                            control={control}
                            name={`tools.${row.id}.${c.key}` as const}
                            render={({ field: { value, onChange } }) => (
                              <Switch
                                checked={!!value}
                                onChange={(e) => onChange(e.target.checked)}
                                disabled={
                                  readOnly || isCancelling || isFetching
                                }
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ))}
            </Box>
          </FormProvider>

          {!isLastUpdateLoading && lastUpdateData?.data && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="grey.500">
                Условия успешно применены к приложениям Service Tools
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatRuDate(lastUpdateData.data.updated_at)}{" "}
                {lastUpdateData.data.user.last_name}{" "}
                {lastUpdateData.data.user.first_name}
              </Typography>
            </Box>
          )}
          {saveChangesModal}
        </>
      )}
    </>
  );
};
